import axios, { AxiosError } from 'axios'
import { useAuthStore } from '../stores/authStore'

/**
 * Shared API client for auth-service
 * - Dev: Uses /api with Vite proxy -> http://localhost:3001
 * - Staging/Prod: Uses VITE_API_URL directly (no proxy needed)
 */
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor: auto-attach auth token
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor: handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const url = error.config?.url || ''

    // On 401, clear auth state (token expired or invalid)
    // Skip for:
    // - /auth/ endpoints to avoid logout loops during login/refresh
    // - /users/me/password - 401 there means "wrong password", not "session expired"
    const isAuthEndpoint = url.startsWith('/auth/')
    const isPasswordChange = url === '/users/me/password'

    if (
      error.response?.status === 401 &&
      !isAuthEndpoint &&
      !isPasswordChange
    ) {
      const { accessToken } = useAuthStore.getState()
      if (accessToken) {
        // Only logout if we thought we were authenticated
        useAuthStore.setState({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        })
      }
    }
    return Promise.reject(error)
  }
)

/**
 * Role in use details for ROLES_IN_USE error
 */
export interface RoleInUse {
  role: string
  userCount: number
}

/**
 * API error response structure
 */
export interface ApiError {
  error: {
    code: string
    message: string
    details?: {
      rolesInUse?: RoleInUse[]
      [key: string]: unknown
    }
  }
}

/**
 * Format ROLES_IN_USE error with details about which roles are in use
 */
function formatRolesInUseError(rolesInUse: RoleInUse[]): string {
  const roleDetails = rolesInUse
    .map((r) => `"${r.role}" (${r.userCount} user${r.userCount === 1 ? '' : 's'})`)
    .join(', ')
  return `Cannot remove roles that are assigned to users: ${roleDetails}`
}

/**
 * Extract user-friendly error message from API error response
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof AxiosError && error.response?.data) {
    const apiError = error.response.data as ApiError

    // Handle ROLES_IN_USE error with detailed formatting
    if (apiError.error?.code === 'ROLES_IN_USE' && apiError.error.details?.rolesInUse?.length) {
      return formatRolesInUseError(apiError.error.details.rolesInUse)
    }

    return apiError.error?.message || 'An unexpected error occurred'
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'An unexpected error occurred'
}
